import React, { useEffect, useState } from 'react';
import './maintenance-schedule-landing.scss';
import DOMPurify from 'dompurify';
import { useMaintenanceScheduleLandingContent } from '../hooks/use-maintenance-schedule-landing-content';
import { EnterYourVinSection } from './enter-your-vin-section/enter-your-vin-section';
import { LoginSection } from '../../login-register/login-register';
import { useLoginOrRegisterContent } from '../../login-register/hooks/use-login-register-content';
import { VehicleFromGarage } from './vehicle-from-garage/vehicle-from-garage';
import { SupportTrio } from '../../../common/support-trio/support-trio';
import { useGenericAppErrorContent } from '../../error-message/hook/use-generic-app-error-content';
import { ErrorMessage } from '../../error-message/error-message';
import { useAnalytics } from '../../../../hooks/use-analytics';
import { BreadcrumbWrapperReimagine } from '../../../../components/common/breadcrumb-wrapper-reimagine/breadcrumb-wrapper-reimagine';
import { ViewTemplate } from '../../view-template/view-template';
import { PageType } from '../../view-template/page-type';
import { useExperienceContent } from '../../../../hooks/use-server-content';
import { FMCardProps } from '@own/fds-react/dist/types/fm-card.types';
import { FMCard, FMCarousel } from '@own/fds-react';
import { FeatureCardsFragment } from '../../vehicle-portal/services/feature-cards/feature-card-article-service';
import AppConfigurationService from '../../../../services/app-configuration-service/app-configuration-service';
import { NewAnalyticsService } from '../../../../services/new-analytics-service/new-analytics-service';
import { URLUtil } from '../../../utils/url-util/url-util';
import { useHistory } from 'react-router-dom';
import { Skeleton } from '../../../common/skeleton/skeleton';
import { ActivityIndicator } from '../../../common/activity-indicator/activity-indicator';
import { VehicleAttributes } from '../../../../models/vehicle-attributes';

interface Props {
    isAuthenticated: string;
    loginClick: (action: string) => void;
    goToMaintenanceScheduleViewDetails: (vin: string) => void;
    page: PageType;
    isWebview: boolean;
    isAuthenticationCompleted: boolean;
    vehiclesData: VehicleAttributes[];
    isUserWithCars: boolean;
}

export const MaintenanceScheduleLanding = (props: Props) => {
    const history = useHistory();
    const genericAppErrorContent = useGenericAppErrorContent();
    const [showError, setShowError] = useState<boolean>(false);
    const maintenanceScheduleLandingContent = useMaintenanceScheduleLandingContent();
    const isContentAvailable = !!Object.keys(maintenanceScheduleLandingContent)
        .length;
    const loginOrRegisterContent = useLoginOrRegisterContent();

    const {
        maintenanceSchedulePageHeadline,
        maintenanceScheduleDescription,
        enterYourVehicleDetailFormHeadline,
        vinOrRegInputFieldLabel,
        mileageInputFieldLabel,
        enterYourVehicleDetailsCTALabel,
        orSeparatorText,
        maintenanceScheduleNotFoundErrorMessage,
        maintenanceSchedulePath,
        enterYourVehicleDetailsTooltipHeadline,
        enterYourVehicleDetailsTooltipContent,
        enterYourVehicleDetailsTooltipAriaLabel,
        chooseYourVehicleFormHeadline,
        yourVehicleFieldLabel,
        chooseYourVehicleCta,
    } = maintenanceScheduleLandingContent;
    const {
        loginOrRegisterFormHeading,
        loginOrRegisterFormDescription,
        signinButtonLabel,
        registerButtonLabel,
    } = loginOrRegisterContent;
    const { genericErrorMessage } = genericAppErrorContent;
    const maintenanceScheduleBreadCrumb = maintenanceSchedulePageHeadline?.toLowerCase();
    const maintenanceScheduleBreadCrumbTitle = maintenanceScheduleBreadCrumb?.replace(
        /(?<=^|\s)\w/g,
        letter => letter.toUpperCase()
    );

    const {
        isAuthenticated,
        isWebview,
        isAuthenticationCompleted,
        vehiclesData,
        isUserWithCars,
    } = props;

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowCarousel(true);
        }, 1000); // Delay in milliseconds (e.g., 2000ms = 2 seconds)
        return () => clearTimeout(timer); // Cleanup the timer on component unmount
    }, []);

    const [authenticatedContent] = useExperienceContent<FeatureCardsFragment>(
        'common',
        'maintenance-schedule-authenticated',
        'articleCarousel'
    );

    const [unAuthenticatedContent] = useExperienceContent<FeatureCardsFragment>(
        'common',
        'maintenance-schedule-unauthenticated',
        'articleCarousel'
    );
    const [fireEvents] = useAnalytics();
    const appConfiguration = new AppConfigurationService().getAppConfiguration();

    function handleFMCardClick(item: FMCardProps, eventName: string) {
        return async () => {
            const referralExit = await new NewAnalyticsService().findMatchingReferralExit(
                item.ctaHref || ''
            );
            eventName &&
                !referralExit &&
                fireEvents(
                    eventName,
                    undefined,
                    {
                        ctaType: item.ctaLabel,
                    },
                    false
                );
            NewAnalyticsService.fireReferralExitEventBasedOnUrl(
                item.ctaHref || '',
                fireEvents
            );
            if (item.ctaHref && URLUtil.isRelativeUrl(item.ctaHref)) {
                history.push(item.ctaHref || '');
            }
        };
    }
    const [showCarousel, setShowCarousel] = useState(false);
    const keepRunningVehicleContent = () => {
        const isLoggedIn = isAuthenticated === 'loggedIn';
        const cardsContent = isLoggedIn
            ? authenticatedContent?.cards
            : unAuthenticatedContent?.cards;
        const containerData = isLoggedIn
            ? authenticatedContent
            : unAuthenticatedContent;
        return (
            cardsContent && (
                <>
                    <div className="fm-maintenance-schedule-header">
                        {containerData?.title && (
                            <h2 className="fm-maintenance-schedule__title">
                                {containerData?.title}
                            </h2>
                        )}
                        {containerData?.subtitle && (
                            <div className="fm-maintenance-schedule__subtitle">
                                {containerData?.subtitle}
                            </div>
                        )}
                    </div>
                    {showCarousel ? (
                        <div className="maintenance-schedule-article-carousel">
                            <FMCarousel
                                items={cardsContent || []}
                                render={(item: FMCardProps) => (
                                    <FMCard
                                        {...item}
                                        imageSrc={`${process.env.REACT_APP_AEM_BASE_URL}${item.imageSrc}`}
                                        ctaType={'primary'}
                                        ctaTheme={'light'}
                                        ctaOnClick={handleFMCardClick(
                                            item,
                                            'maintenance-schedule-keep-your-vehicle-running-cta-onclick'
                                        )}
                                        imageLoading="lazy"
                                        imageFetchPriority="high"
                                        imageDecoding="async"
                                        imageStyles={{
                                            width: '100%',
                                            height: '100%',
                                            aspectRatio: '17/9',
                                        }}
                                    >
                                        {item?.children}
                                    </FMCard>
                                )}
                                {...{
                                    ...containerData,
                                    title: '',
                                    subtitle: '',
                                }}
                            />
                        </div>
                    ) : (
                        <Skeleton width={1200} height={400} />
                    )}
                </>
            )
        );
    };

    const vehicleDetailsFormLeftClass =
        isWebview && isAuthenticated !== 'loggedIn'
            ? 'vehicle-details-form-left-hide'
            : 'vehicle-details-form-left';

    return (
        <ViewTemplate
            page={props.page}
            className="trailer-checklist-view"
            hideLinksList={true}
            hideSearchBar={isWebview}
        >
            {isContentAvailable && (
                <>
                    {isWebview ? (
                        ''
                    ) : (
                        <BreadcrumbWrapperReimagine
                            currentPage={maintenanceScheduleBreadCrumbTitle}
                            hashId="support"
                        />
                    )}
                    <section className="maintenance-schedule-landing-page">
                        <section className="maintenance-schedule-landing-heading-section">
                            <h1 className="maintenance-schedule-landing-header">
                                {' '}
                                {maintenanceSchedulePageHeadline}{' '}
                            </h1>
                            {!showError && (
                                <div
                                    className="maintenance-schedule-landing-description"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            maintenanceScheduleDescription
                                        ),
                                    }}
                                />
                            )}
                        </section>
                        {showError ? (
                            <div className="maintenance-error">
                                <ErrorMessage
                                    errorMessage={genericErrorMessage}
                                />
                            </div>
                        ) : isAuthenticationCompleted ? (
                            <section className="enter-your-vin-combined-wrapper">
                                <div className="vehicle-details-wrapper">
                                    {(isUserWithCars ||
                                        isAuthenticated === 'notLoggedIn') && (
                                        <>
                                            {' '}
                                            <div
                                                className={
                                                    vehicleDetailsFormLeftClass
                                                }
                                            >
                                                {isUserWithCars ? (
                                                    <VehicleFromGarage
                                                        vehiclesData={
                                                            vehiclesData
                                                        }
                                                        showErrorPage={isError =>
                                                            setShowError(
                                                                isError
                                                            )
                                                        }
                                                        chooseYourVehicleFormHeadline={
                                                            chooseYourVehicleFormHeadline
                                                        }
                                                        yourVehicleFieldLabel={
                                                            yourVehicleFieldLabel
                                                        }
                                                        chooseYourVehicleCta={
                                                            chooseYourVehicleCta
                                                        }
                                                        maintenanceScheduleNotFoundErrorMessage={
                                                            maintenanceScheduleNotFoundErrorMessage
                                                        }
                                                        goToMaintenanceScheduleViewDetails={
                                                            props.goToMaintenanceScheduleViewDetails
                                                        }
                                                    />
                                                ) : (
                                                    !isWebview && (
                                                        <LoginSection
                                                            contentLabels={{
                                                                loginOrRegisterFormHeading,
                                                                loginOrRegisterFormDescription,
                                                                signinButtonLabel,
                                                                registerButtonLabel,
                                                            }}
                                                            loginClick={
                                                                props.loginClick
                                                            }
                                                        />
                                                    )
                                                )}
                                            </div>
                                            {(!isWebview ||
                                                (isWebview &&
                                                    isAuthenticated ===
                                                        'loggedIn')) && (
                                                <div
                                                    className={`divider-section ${
                                                        isAuthenticated ===
                                                        'loggedIn'
                                                            ? 'divider-logged-in'
                                                            : ''
                                                    }`}
                                                >
                                                    <hr className="before-dash" />
                                                    <span>
                                                        {orSeparatorText}
                                                    </span>
                                                    <hr className="after-dash" />
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {((showCarousel &&
                                        isAuthenticated !== 'loggedIn') ||
                                        isUserWithCars) && (
                                        <EnterYourVinSection
                                            contentLabels={{
                                                enterYourVehicleDetailFormHeadline,
                                                vinOrRegInputFieldLabel,
                                                mileageInputFieldLabel,
                                                enterYourVehicleDetailsCTALabel,
                                                maintenanceScheduleNotFoundErrorMessage,
                                                maintenanceSchedulePath,
                                                enterYourVehicleDetailsTooltipHeadline,
                                                enterYourVehicleDetailsTooltipContent,
                                                enterYourVehicleDetailsTooltipAriaLabel,
                                            }}
                                            showErrorPage={isError =>
                                                setShowError(isError)
                                            }
                                            goToMaintenanceScheduleViewDetails={
                                                props.goToMaintenanceScheduleViewDetails
                                            }
                                        />
                                    )}
                                </div>
                            </section>
                        ) : (
                            <ActivityIndicator className="half-height" />
                        )}
                    </section>
                    {!isWebview &&
                        appConfiguration.countryCode === 'us' &&
                        keepRunningVehicleContent()}
                    {!isWebview && appConfiguration.countryCode !== 'us' && (
                        <SupportTrio
                            folder="common"
                            experienceFragmentName="maintenance-schedule-support"
                        />
                    )}
                </>
            )}
        </ViewTemplate>
    );
};
